import { Button } from '@framework/ui/atoms/Button';
import { useCallback, useState } from 'react';
import { ApplyLicenseModal } from './ApplyLicenseModal';
import { StandardPaymentMethodModal } from './StandardPaymentMethodModal';
import type { GroupEntity } from '@group/domain';
import { ApplyLicenseThanksModal } from './ApplyLicenseThanksModal';
import { Trans, useTranslation } from 'react-i18next';
import { STANDARD_LICENSE_MONTHLY_FEE } from './licensePricing';

type Props = {
  group: GroupEntity;
  canUseCardCheckout: boolean;
  isTrial: boolean;
};

type LicenseKindKey =
  | 'group.licenseDescription.kindStandard'
  | 'group.licenseDescription.kindBulk25'
  | 'group.licenseDescription.kindBulk50'
  | 'group.licenseDescription.kindBulk100';

export type License = {
  kindLabelKey: LicenseKindKey;
  submissionValue: string;
  number: number;
  editable: boolean;
  monthlyFee: number;
};

const STANDARD_LICENSE: License = {
  kindLabelKey: 'group.licenseDescription.kindStandard',
  submissionValue: 'スタンダード',
  number: 1,
  editable: true,
  monthlyFee: STANDARD_LICENSE_MONTHLY_FEE,
};

const BULK_25_LICENSE: License = {
  kindLabelKey: 'group.licenseDescription.kindBulk25',
  submissionValue: 'バルクライセンス（25ユーザまで）',
  number: 25,
  editable: false,
  monthlyFee: 30000,
};

const BULK_50_LICENSE: License = {
  kindLabelKey: 'group.licenseDescription.kindBulk50',
  submissionValue: 'バルクライセンス（50ユーザまで）',
  number: 50,
  editable: false,
  monthlyFee: 50000,
};

const BULK_100_LICENSE: License = {
  kindLabelKey: 'group.licenseDescription.kindBulk100',
  submissionValue: 'バルクライセンス（100ユーザまで）',
  number: 100,
  editable: false,
  monthlyFee: 80000,
};

const AvailableIcon: React.FC = () => <span className="text-lg text-green-600">&#9675;</span>;
const UnavailableIcon: React.FC = () => <span className="text-lg text-gray-400">&times;</span>;

type FeatureComparisonRowProps = {
  feature: string;
  free: React.ReactNode;
  standard: React.ReactNode;
  bulk: React.ReactNode;
};

const FeatureComparisonRow: React.FC<FeatureComparisonRowProps> = ({
  feature,
  free,
  standard,
  bulk,
}: FeatureComparisonRowProps) => (
  <tr>
    <td className="border p-3 text-center">{feature}</td>
    <td className="border p-3 text-center">{free}</td>
    <td className="border p-3 text-center">{standard}</td>
    <td className="border p-3 text-center">{bulk}</td>
  </tr>
);

export const LicenseDescriptionPanel: React.FC<Props> = ({ group, canUseCardCheckout, isTrial }: Props) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showThanks, setShowThanks] = useState<boolean>(false);

  const [license, setLicense] = useState<License>(STANDARD_LICENSE);

  const handleApply = (selectedLicense: License) => {
    setLicense(selectedLicense);
    if (selectedLicense === STANDARD_LICENSE && canUseCardCheckout) {
      setShowPaymentMethodModal(true);
    } else {
      setShowModal(true);
    }
  };

  const onApplyCompleted = useCallback(() => {
    setShowModal(false);
    setShowThanks(true);
  }, []);

  const handleCloseModal = useCallback(() => setShowModal(false), []);
  const handleCloseThanksModal = useCallback(() => setShowThanks(false), []);

  return (
    <div className="mr-30 mt-10">
      <h2 className="mb-4 text-lg font-bold">{t('group.licenseDescription.title')}</h2>
      <div className="flex w-full flex-row justify-center">
        <div className="w-1/4 rounded-lg border-2" style={{ height: '400px' }}>
          <div className="rounded-t-lg bg-gray-500 p-4 text-xl font-bold text-white" style={{ height: '70px' }}>
            {t('group.licenseDescription.freePlan.title')}
          </div>
          <div className="bg-gray-500 p-4 text-sm text-white" style={{ height: '80px' }}>
            {t('group.licenseDescription.freePlan.subtitle')}
          </div>
          <div>
            <div className="my-4 flex flex-col justify-between" style={{ height: '200px' }}>
              <div className="w-full">
                <div className="mx-7 mt-5">{t('group.licenseDescription.perUserMonth')}</div>
                <div className="mx-7 text-xl font-bold">{t('group.licenseDescription.freePlan.price')}</div>
              </div>
              <div className="mx-4 mb-2 text-xs text-gray-600">
                <ul className="list-disc space-y-1 pl-4">
                  <li>{t('group.licenseDescription.freePlan.restriction1')}</li>
                  <li>{t('group.licenseDescription.freePlan.restriction2')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="ml-4 w-1/4 rounded-lg border-2" style={{ height: '400px' }}>
          <div className="rounded-t-lg bg-brand p-4 text-xl font-bold text-white" style={{ height: '70px' }}>
            {t('group.licenseDescription.standardPlan.title')}
          </div>
          <div className="bg-brand p-4 text-white" style={{ height: '80px' }}>
            {t('group.licenseDescription.standardPlan.subtitle')}
          </div>
          <div>
            <div className="my-4 flex flex-row" style={{ height: '200px' }}>
              <div className="w-full">
                <div className="mx-7 mt-5">{t('group.licenseDescription.perUserMonth')}</div>
                <div className="mx-7 text-xl font-bold">{t('group.licenseDescription.standardPlan.price')}</div>
                <div className="mx-7 mt-14">
                  <Button className="w-full" color="brand" onClick={() => handleApply(STANDARD_LICENSE)}>
                    {t('group.licenseDescription.apply')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="ml-4 w-3/4 rounded-lg border-2" style={{ height: '400px' }}>
          <div className="rounded-t-lg bg-gray-700 p-4 text-xl font-bold text-white" style={{ height: '70px' }}>
            {t('group.licenseDescription.bulkPlan.title')}
          </div>
          <div className="bg-gray-700 p-4 align-top text-white" style={{ height: '80px' }}>
            {t('group.licenseDescription.bulkPlan.subtitle1')}
            <br />
            {t('group.licenseDescription.bulkPlan.subtitle2')}
          </div>
          <div>
            <div className="my-4 flex flex-row" style={{ height: '200px' }}>
              <div className="w-1/3 border-r-2">
                <div className="mx-7 mt-5">{t('group.licenseDescription.bulk25.users')}</div>
                <div className="mx-7 text-xl font-bold">{t('group.licenseDescription.bulk25.price')}</div>
                <div className="mx-7 mt-14">
                  <Button className="w-full" color="brand" onClick={() => handleApply(BULK_25_LICENSE)}>
                    {t('group.licenseDescription.apply')}
                  </Button>
                </div>
              </div>
              <div className="w-1/3 border-r-2">
                <div className="mx-7 mt-5">{t('group.licenseDescription.bulk50.users')}</div>
                <div className="mx-7 text-xl font-bold">{t('group.licenseDescription.bulk50.price')}</div>
                <div className="mx-7 mt-14">
                  <Button className="w-full" color="brand" onClick={() => handleApply(BULK_50_LICENSE)}>
                    {t('group.licenseDescription.apply')}
                  </Button>
                </div>
              </div>
              <div className="w-1/3">
                <div className="mx-7 mt-5">{t('group.licenseDescription.bulk100.users')}</div>
                <div className="mx-7 text-xl font-bold">{t('group.licenseDescription.bulk100.price')}</div>
                <div className="mx-7 mt-14">
                  <Button className="w-full" color="brand" onClick={() => handleApply(BULK_100_LICENSE)}>
                    {t('group.licenseDescription.apply')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 機能比較テーブル */}
      <div className="ml-4 mt-10">
        <h3 className="mb-4 text-lg font-bold">{t('group.licenseDescription.comparison.title')}</h3>
        <table className="w-full table-fixed border-collapse border text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="w-[28%] border p-3 text-center">{t('group.licenseDescription.comparison.colFeature')}</th>
              <th className="w-[24%] border p-3 text-center">{t('group.licenseDescription.comparison.colFree')}</th>
              <th className="w-[24%] border p-3 text-center">{t('group.licenseDescription.comparison.colStandard')}</th>
              <th className="w-[24%] border p-3 text-center">{t('group.licenseDescription.comparison.colBulk')}</th>
            </tr>
          </thead>
          <tbody>
            <FeatureComparisonRow
              feature={t('group.licenseDescription.feature.viewModel')}
              free={<span>{t('group.licenseDescription.feature.viewModelFreeLimit')}</span>}
              standard={<AvailableIcon />}
              bulk={<AvailableIcon />}
            />
            <FeatureComparisonRow
              feature={t('group.licenseDescription.feature.imageExport')}
              free={<UnavailableIcon />}
              standard={<AvailableIcon />}
              bulk={<AvailableIcon />}
            />
            <FeatureComparisonRow
              feature={t('group.licenseDescription.feature.fileUpload')}
              free={<UnavailableIcon />}
              standard={<AvailableIcon />}
              bulk={<AvailableIcon />}
            />
            <FeatureComparisonRow
              feature={t('group.licenseDescription.feature.publicShare')}
              free={<UnavailableIcon />}
              standard={<AvailableIcon />}
              bulk={<AvailableIcon />}
            />
            <FeatureComparisonRow
              feature={t('group.licenseDescription.feature.ipRestriction')}
              free={<UnavailableIcon />}
              standard={<AvailableIcon />}
              bulk={<AvailableIcon />}
            />
          </tbody>
        </table>
      </div>
      <div className="my-10 ml-4 text-sm">
        <ul className="space-y-1">
          <li>{t('group.licenseDescription.note.taxExcluded')}</li>
          <li>{t('group.licenseDescription.note.trialExpiry')}</li>
          <li>{t('group.licenseDescription.note.planExpiry')}</li>
          <li>{t('group.licenseDescription.note.invoiceSent')}</li>
          <li>{t('group.licenseDescription.note.upgradeEffect')}</li>
          <li>
            <Trans
              i18nKey="group.licenseDescription.note.over100Users"
              components={{
                contactLink: (
                  <a
                    href="https://balus-help.levii.co.jp/hc/ja/requests/new"
                    className="text-blue-700 underline hover:no-underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </li>
        </ul>
      </div>
      {showPaymentMethodModal && canUseCardCheckout && (
        <StandardPaymentMethodModal
          groupId={group.id}
          isTrial={isTrial}
          onClose={() => setShowPaymentMethodModal(false)}
          onSelectInvoice={() => {
            setShowPaymentMethodModal(false);
            setShowModal(true);
          }}
        />
      )}
      <ApplyLicenseModal
        isOpen={showModal}
        onApplyCompleted={onApplyCompleted}
        onClose={handleCloseModal}
        group={group}
        license={license}
      />
      <ApplyLicenseThanksModal isOpen={showThanks} onClose={handleCloseThanksModal} />
    </div>
  );
};
